import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Eye, Send, Printer, Loader2, CheckCircle2, AlertTriangle, BarChart3, Paperclip } from 'lucide-react';
import { Button } from '../ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { API_CP, Carte, TitreCarte, dateFR, nomComplet, qualiteOrg } from './constantes';

const manquant = (c) => (!c.date ? 'la date de la séance (onglet Séance)' : !c.ordre_du_jour.length ? "l'ordre du jour" : !c.participants.length ? 'les participants' : null);

const LigneConvocation = ({ p, onApercu, onEnvoyer, occupe }) => (
  <li className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center" data-testid={`cp-convocation-${p.participant_id}`}>
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm font-semibold text-[#0F172A]">{nomComplet(p)}</p>
      <p className="truncate text-xs text-slate-500">{qualiteOrg(p)}{p.email ? ` · ${p.email}` : ' · pas d\'e-mail'}</p>
    </div>
    {p.convoque_le
      ? <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700" data-testid={`cp-convocation-envoyee-${p.participant_id}`}><CheckCircle2 className="h-3.5 w-3.5" /> Envoyée le {dateFR(p.convoque_le)}</span>
      : <span className="text-xs text-slate-400">Non envoyée</span>}
    <div className="flex gap-2">
      <Button size="sm" variant="outline" onClick={onApercu} data-testid={`cp-convocation-apercu-${p.participant_id}`}><Eye className="mr-1.5 h-3.5 w-3.5" /> Aperçu</Button>
      <Button size="sm" variant="outline" onClick={onEnvoyer} disabled={occupe} data-testid={`cp-convocation-envoyer-${p.participant_id}`}><Send className="mr-1.5 h-3.5 w-3.5" /> Envoyer</Button>
    </div>
  </li>
);

export const OngletConvocations = ({ cp, onPdf }) => {
  const { conseil, recharger } = cp;
  const [progres, setProgres] = useState(null);
  const [confirmer, setConfirmer] = useState(false);
  const api = API_CP(conseil.annee);
  const manque = manquant(conseil);

  const envoyer = async (ids) => {
    const bilan = { emails: 0, notifications: 0, sans: [], echecs: [] };
    for (const [k, id] of ids.entries()) {
      setProgres(`${k + 1}/${ids.length}`);
      try {
        const { data } = await axios.post(`${api}/convocations/envoyer`, { participant_ids: [id] });
        bilan.emails += data.emails; bilan.notifications += data.notifications; bilan.sans.push(...data.sans_email); bilan.echecs.push(...data.erreurs);
      } catch (e) { bilan.echecs.push(nomComplet(conseil.participants.find((p) => p.participant_id === id))); }
    }
    setProgres(null);
    await recharger();
    const texte = `${bilan.emails} e-mail(s) et ${bilan.notifications} notification(s) envoyé(s)`
      + (bilan.sans.length ? ` · sans e-mail : ${bilan.sans.join(', ')}` : '') + (bilan.echecs.length ? ` · échec : ${bilan.echecs.join(', ')}` : '');
    toast[bilan.echecs.length ? 'warning' : 'success'](texte);
  };

  return (
    <Carte data-testid="cp-convocations">
      <TitreCarte Icone={Mail} titre="Convocations" sousTitre="Une convocation par personne sur papier à en-tête FISL, envoyée par e-mail (PDF joint) avec une notification dans l'application.">
        <Button variant="outline" onClick={() => onPdf(null, `Bilan d'activité ${conseil.annee}`, `${api}/bilan-activite.pdf`)} data-testid="cp-convocations-bilan">
          <BarChart3 className="mr-2 h-4 w-4" /> Bilan d'activité
        </Button>
        <Button variant="outline" disabled={!!manque} onClick={() => onPdf(null, 'Convocations', `${api}/convocations.pdf`)} data-testid="cp-convocations-imprimer"><Printer className="mr-2 h-4 w-4" /> Imprimer toutes</Button>
        <Button disabled={!!manque || !!progres} onClick={() => setConfirmer(true)} className="bg-[#0F172A]" data-testid="cp-convocations-envoyer-tous">
          {progres ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Envoi {progres}</> : <><Send className="mr-2 h-4 w-4" /> Envoyer à tous</>}
        </Button>
      </TitreCarte>
      {manque && <p className="mb-4 flex items-center gap-2 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="cp-convocations-manque"><AlertTriangle className="h-4 w-4" /> Complétez d'abord {manque}.</p>}
      <p className="mb-4 flex items-start gap-2 rounded-xl bg-[#8DC63F]/10 px-4 py-3 text-sm text-slate-700" data-testid="cp-convocations-bilan-info">
        <Paperclip className="mt-0.5 h-4 w-4 shrink-0 text-[#6FA22E]" /> Le bilan d'activité {conseil.annee} (chiffres de l'application) est joint automatiquement à chaque e-mail de convocation.
      </p>
      <ul className="divide-y">
        {conseil.participants.map((p) => (
          <LigneConvocation key={p.participant_id} p={p} occupe={!!manque || !!progres}
            onApercu={() => onPdf(null, `Convocation – ${nomComplet(p)}`, `${api}/convocations.pdf?participant_id=${p.participant_id}`)}
            onEnvoyer={() => envoyer([p.participant_id])} />
        ))}
      </ul>
      <AlertDialog open={confirmer} onOpenChange={setConfirmer}>
        <AlertDialogContent data-testid="cp-convocations-confirmation">
          <AlertDialogHeader>
            <AlertDialogTitle>Envoyer {conseil.participants.length} convocation(s) ?</AlertDialogTitle>
            <AlertDialogDescription>Chaque participant reçoit un e-mail avec sa convocation et le bilan d'activité en PDF, et une notification s'il a un compte dans l'application.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cp-convocations-annuler">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={() => envoyer(conseil.participants.map((p) => p.participant_id))} data-testid="cp-convocations-confirmer">Envoyer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Carte>
  );
};
